import { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '@/storybook/components/Button/Button';
import Text from '@/storybook/components/Text/Text';
import AIFilterChips, { type AIFilterChip } from '../AIFilterChips/AIFilterChips';
import { COLLAPSIBLE_FILTER_GROUP_TEST_IDS } from './testIds';
import styles from './CollapsibleFilterGroup.module.css';

const DEFAULT_MAX_VISIBLE_CHIPS = 6;

export interface CollapsibleFilterGroupProps {
  label: string;
  filters: ReadonlyArray<AIFilterChip>;
  maxVisibleCount?: number;
  dataTestId?: string;
}

const CollapsibleFilterGroup: FC<CollapsibleFilterGroupProps> = ({
  label,
  filters,
  maxVisibleCount = DEFAULT_MAX_VISIBLE_CHIPS,
  dataTestId = COLLAPSIBLE_FILTER_GROUP_TEST_IDS.base,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded(previousIsExpanded => !previousIsExpanded);
  }, []);

  if (filters.length === 0) {
    return null;
  }

  const hasOverflow = filters.length > maxVisibleCount;
  const visibleFilters = isExpanded || !hasOverflow ? filters : filters.slice(0, maxVisibleCount);
  const hiddenFilterCount = filters.length - maxVisibleCount;

  const overflowToggle = hasOverflow ? (
    <Button
      variant="text"
      size="XS"
      className={styles.toggle}
      aria-expanded={isExpanded}
      onClick={handleToggleExpanded}
      data-testid={`${dataTestId}-toggle`}
    >
      {isExpanded
        ? t('global.showLess')
        : t('components.aiChatDrawer.filterHeader.showMore', { hiddenCount: hiddenFilterCount })}
    </Button>
  ) : null;

  return (
    <div className={styles.group} data-testid={`${dataTestId}-group`}>
      <Text as="span" className={styles.groupLabel} data-testid={`${dataTestId}-label`}>
        {label}
      </Text>
      <AIFilterChips
        filters={visibleFilters}
        className={styles.chipList}
        data-testid={`${dataTestId}-chips`}
        trailingSlot={overflowToggle}
      />
    </div>
  );
};

export default CollapsibleFilterGroup;
